import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Trash, Translate, FilmStrip, TagSimple, SpinnerGap, Globe, Robot, TextT, MapPin, Lightning, Upload, Calendar, Eye, Play, Pause, FileText, ChartBar, CloudArrowUp, Article } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [contents, setContents] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [siteTexts, setSiteTexts] = useState([]);
  const [scrapeJobs, setScrapeJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentDialog, setContentDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [translating, setTranslating] = useState({});
  const [batchTranslating, setBatchTranslating] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [translatingTexts, setTranslatingTexts] = useState(false);
  const [scrapeForm, setScrapeForm] = useState({ url: "https://www.analvids.com/", pages: 1, download_thumbnails: true });
  const [contentForm, setContentForm] = useState({ title: "", description: "", thumbnail_url: "", video_url: "", duration: "", tags: "", categories: "" });
  const [categoryForm, setCategoryForm] = useState({ name: "", slug: "", description: "" });
  const [editingLang, setEditingLang] = useState(null);
  const [editTexts, setEditTexts] = useState({});
  const [saving, setSaving] = useState(false);
  const [bulkJson, setBulkJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [gscConfig, setGscConfig] = useState({ verification_code: "", property_url: "" });
  const [syncingCats, setSyncingCats] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [cdnConfig, setCdnConfig] = useState({ cdn_subdomain: "", cdn_enabled: false });
  const [rewritingCdn, setRewritingCdn] = useState(false);
  const [seoArticles, setSeoArticles] = useState([]);
  const [generatingSeo, setGeneratingSeo] = useState(false);
  const [editingSeoSlug, setEditingSeoSlug] = useState(null);
  const [editingSeoContent, setEditingSeoContent] = useState("");

  const fetchProject = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(data);
    } catch { navigate("/projects"); }
  }, [projectId, navigate]);

  const fetchContents = useCallback(async (page = 1) => {
    try {
      let url = `${API}/projects/${projectId}/search?page=${page}&limit=20`;
      if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;
      if (filterTag) url += `&tag=${encodeURIComponent(filterTag)}`;
      if (filterCategory) url += `&category=${encodeURIComponent(filterCategory)}`;
      if (filterStatus) url += `&status=${encodeURIComponent(filterStatus)}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setContents(data);
      if (data.available_tags) setAvailableTags(data.available_tags);
      if (data.available_categories) setAvailableCategories(data.available_categories);
    } catch (err) { console.error(err); }
  }, [projectId, searchQuery, filterTag, filterCategory, filterStatus]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/categories`, { withCredentials: true });
      setCategories(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchSiteTexts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/site-texts`, { withCredentials: true });
      setSiteTexts(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchGscConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/gsc-config`, { withCredentials: true });
      setGscConfig(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/analytics?days=30`, { withCredentials: true });
      setAnalytics(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchCdnConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/cdn-config`, { withCredentials: true });
      setCdnConfig(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchSeoArticles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/seo-articles`, { withCredentials: true });
      setSeoArticles(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  const fetchScrapeJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/scrape-jobs`, { withCredentials: true });
      setScrapeJobs(data);
    } catch (err) { console.error(err); }
  }, [projectId]);

  useEffect(() => {
    Promise.all([fetchProject(), fetchContents(), fetchCategories(), fetchSiteTexts(), fetchScrapeJobs(), fetchGscConfig(), fetchAnalytics(), fetchCdnConfig(), fetchSeoArticles()]).finally(() => setLoading(false));
  }, [fetchProject, fetchContents, fetchCategories, fetchSiteTexts, fetchScrapeJobs, fetchGscConfig, fetchAnalytics, fetchCdnConfig, fetchSeoArticles]);

  const handleCreateContent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...contentForm, tags: contentForm.tags ? contentForm.tags.split(",").map(t => t.trim()) : [], categories: contentForm.categories ? contentForm.categories.split(",").map(c => c.trim()) : [] };
      await axios.post(`${API}/projects/${projectId}/contents`, payload, { withCredentials: true });
      toast.success("Content added");
      setContentDialog(false);
      setContentForm({ title: "", description: "", thumbnail_url: "", video_url: "", duration: "", tags: "", categories: "" });
      fetchContents(); fetchProject();
    } catch { toast.error("Failed to add content"); }
    finally { setSaving(false); }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/categories`, categoryForm, { withCredentials: true });
      toast.success("Category created");
      setCategoryDialog(false);
      setCategoryForm({ name: "", slug: "", description: "" });
      fetchCategories();
    } catch { toast.error("Failed to create category"); }
    finally { setSaving(false); }
  };

  const handleDeleteContent = async (id) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/contents/${id}`, { withCredentials: true });
      toast.success("Content deleted");
      fetchContents(); fetchProject();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/categories/${id}`, { withCredentials: true });
      toast.success("Category deleted");
      fetchCategories();
    } catch { toast.error("Failed to delete"); }
  };

  const handleTranslate = async (contentId) => {
    setTranslating(prev => ({ ...prev, [contentId]: true }));
    try {
      await axios.post(`${API}/projects/${projectId}/contents/${contentId}/translate`, {}, { withCredentials: true });
      toast.success("Translation completed");
      fetchContents();
    } catch { toast.error("Translation failed"); }
    finally { setTranslating(prev => ({ ...prev, [contentId]: false })); }
  };

  const handleBatchTranslate = async () => {
    setBatchTranslating(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/batch-translate`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch { toast.error("Batch translation failed"); }
    finally { setBatchTranslating(false); }
  };

  const handleScrape = async () => {
    setScraping(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/scrape`, scrapeForm, { withCredentials: true });
      toast.success(data.message);
      setTimeout(() => { fetchScrapeJobs(); fetchContents(); fetchProject(); }, 3000);
    } catch { toast.error("Scraping failed"); }
    finally { setScraping(false); }
  };

  const handleTranslateAllTexts = async () => {
    setTranslatingTexts(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/site-texts/translate-all`, {}, { withCredentials: true });
      toast.success(data.message);
      setTimeout(fetchSiteTexts, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || "Translation failed"); }
    finally { setTranslatingTexts(false); }
  };

  const handleSaveSiteTexts = async () => {
    if (!editingLang) return;
    try {
      await axios.put(`${API}/projects/${projectId}/site-texts/${editingLang}`, { texts: editTexts }, { withCredentials: true });
      toast.success(`${editingLang} texts saved`);
      setEditingLang(null);
      fetchSiteTexts();
    } catch { toast.error("Failed to save"); }
  };

  const startEditTexts = (lang, texts) => {
    setEditingLang(lang);
    setEditTexts({ ...texts });
  };

  const handleBulkImport = async () => {
    setImporting(true);
    try {
      const items = JSON.parse(bulkJson);
      const payload = Array.isArray(items) ? items : [items];
      const { data } = await axios.post(`${API}/projects/${projectId}/bulk-import`, { items: payload }, { withCredentials: true });
      toast.success(data.message);
      setBulkJson("");
      fetchContents(); fetchProject();
    } catch (err) {
      if (err instanceof SyntaxError) toast.error("Invalid JSON format");
      else toast.error(err.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const handleSchedule = async (contentId, date) => {
    try {
      await axios.post(`${API}/projects/${projectId}/contents/${contentId}/schedule?scheduled_at=${date}`, {}, { withCredentials: true });
      toast.success("Content scheduled");
      fetchContents();
    } catch { toast.error("Scheduling failed"); }
  };

  const handlePublish = async (contentId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/contents/${contentId}/publish`, {}, { withCredentials: true });
      toast.success("Content published");
      fetchContents();
    } catch { toast.error("Publish failed"); }
  };

  const handleDraft = async (contentId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/contents/${contentId}/draft`, {}, { withCredentials: true });
      toast.success("Content set to draft");
      fetchContents();
    } catch { toast.error("Failed"); }
  };

  const handleSaveGsc = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}/gsc-config`, gscConfig, { withCredentials: true });
      toast.success("GSC config saved");
    } catch { toast.error("Failed to save GSC config"); }
  };

  const handleSyncCategories = async () => {
    setSyncingCats(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/sync-categories`, {}, { withCredentials: true });
      toast.success(data.message);
      setTimeout(() => { fetchCategories(); fetchContents(); }, 2000);
    } catch { toast.error("Sync failed"); }
    finally { setSyncingCats(false); }
  };

  const handleSaveCdn = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}/cdn-config`, cdnConfig, { withCredentials: true });
      toast.success("CDN config saved");
      fetchCdnConfig();
    } catch { toast.error("Failed to save CDN config"); }
  };

  const handleRewriteCdnUrls = async () => {
    setRewritingCdn(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/rewrite-cdn-urls`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchContents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setRewritingCdn(false); }
  };

  const handleGenerateSeoArticles = async () => {
    setGeneratingSeo(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/generate-seo-articles`, {}, { withCredentials: true });
      toast.success(data.message);
      setTimeout(fetchSeoArticles, 10000);
    } catch { toast.error("Failed"); }
    finally { setGeneratingSeo(false); }
  };

  const handleSaveSeoArticle = async () => {
    if (!editingSeoSlug) return;
    try {
      await axios.put(`${API}/projects/${projectId}/seo-articles/${editingSeoSlug}`, { content: editingSeoContent }, { withCredentials: true });
      toast.success("Article saved");
      setEditingSeoSlug(null);
      fetchSeoArticles();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-[#A1A1AA] text-sm p-8">Loading...</div>;
  if (!project) return null;

  const pendingCount = contents.items.filter(c => c.translation_status === "pending").length;

  return (
    <div data-testid="project-detail-page" className="space-y-6">
      <div className="flex items-center gap-4">
        <button data-testid="back-to-projects" onClick={() => navigate("/projects")} className="text-[#A1A1AA] hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>{project.name}</h1>
          <p className="text-sm text-[#A1A1AA]">{project.domain} &middot; {project.target_language} &middot; {project.target_country}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Contents</div>
          <div className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Chivo' }}>{contents.total}</div>
        </div>
        <div className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Categories</div>
          <div className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Chivo' }}>{categories.length}</div>
        </div>
        <div className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Languages</div>
          <div className="text-2xl font-black text-[#4361EE] mt-1" style={{ fontFamily: 'Chivo' }}>{siteTexts.length}</div>
        </div>
        <div className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Jobs</div>
          <div className="text-2xl font-black text-amber-400 mt-1" style={{ fontFamily: 'Chivo' }}>{scrapeJobs.length}</div>
        </div>
      </div>

      <Tabs defaultValue="contents" className="space-y-4">
        <TabsList className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-1 flex-wrap h-auto gap-1">
          <TabsTrigger value="contents" data-testid="tab-contents" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><FilmStrip size={13} className="mr-1" />Contents</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><TagSimple size={13} className="mr-1" />Categories</TabsTrigger>
          <TabsTrigger value="scraper" data-testid="tab-scraper" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><Robot size={13} className="mr-1" />Scraper</TabsTrigger>
          <TabsTrigger value="site-texts" data-testid="tab-site-texts" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><TextT size={13} className="mr-1" />Site Texts</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><MapPin size={13} className="mr-1" />SEO</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><Upload size={13} className="mr-1" />Import</TabsTrigger>
          <TabsTrigger value="template" data-testid="tab-template" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><Eye size={13} className="mr-1" />Template</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><ChartBar size={13} className="mr-1" />Analytics</TabsTrigger>
          <TabsTrigger value="cdn" data-testid="tab-cdn" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><CloudArrowUp size={13} className="mr-1" />CDN</TabsTrigger>
          <TabsTrigger value="seo-articles" data-testid="tab-seo-articles" className="rounded-sm text-xs data-[state=active]:bg-[#E53935] data-[state=active]:text-white"><Article size={13} className="mr-1" />SEO Articles</TabsTrigger>
        </TabsList>

        {/* Contents Tab */}
        <TabsContent value="contents" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <Button data-testid="batch-translate-btn" onClick={handleBatchTranslate} disabled={batchTranslating} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-4 text-sm font-semibold">
              {batchTranslating ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <Lightning size={14} className="mr-1.5" />}
              Batch Translate
            </Button>
            <Button data-testid="add-content-btn" onClick={() => setContentDialog(true)} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-9 px-4 text-sm font-semibold">
              <Plus size={14} className="mr-1.5" /> Add Content
            </Button>
          </div>
          {/* Search & Filter Bar */}
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-3 flex flex-wrap gap-2 items-center">
            <Input data-testid="search-input" placeholder="Search contents..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchContents(1)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-8 text-xs w-48" />
            {availableTags.length > 0 && (
              <select data-testid="filter-tag" value={filterTag} onChange={e => { setFilterTag(e.target.value); }} className="bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm h-8 text-xs px-2">
                <option value="">All Tags</option>
                {availableTags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {availableCategories.length > 0 && (
              <select data-testid="filter-category" value={filterCategory} onChange={e => { setFilterCategory(e.target.value); }} className="bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm h-8 text-xs px-2">
                <option value="">All Categories</option>
                {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <select data-testid="filter-status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }} className="bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm h-8 text-xs px-2">
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
            <Button data-testid="apply-filters-btn" onClick={() => fetchContents(1)} className="bg-[#2A2A2E] hover:bg-[#3A3A3E] text-white rounded-sm h-8 px-3 text-xs">Filter</Button>
            {(searchQuery || filterTag || filterCategory || filterStatus) && (
              <Button onClick={() => { setSearchQuery(""); setFilterTag(""); setFilterCategory(""); setFilterStatus(""); setTimeout(() => fetchContents(1), 100); }} variant="ghost" className="text-[#A1A1AA] hover:text-white rounded-sm h-8 px-2 text-xs">Clear</Button>
            )}
          </div>
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-[#2A2A2E]">
                <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Title</th>
                <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-4 py-3 hidden md:table-cell">Duration</th>
                <th className="text-center text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Status</th>
                <th className="text-right text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-[#2A2A2E]">
                {contents.items.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-[#555]">No content yet.</td></tr>
                ) : contents.items.map((c) => (
                  <tr key={c.id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {c.thumbnail_url && <img src={c.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded-sm bg-[#0A0A0C] flex-shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-sm text-white font-medium truncate max-w-xs">{c.title}</div>
                          {c.translated_title && <div className="text-xs text-[#4361EE] mt-0.5 truncate max-w-xs">{c.translated_title}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#A1A1AA] hidden md:table-cell">{c.duration || "-"}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${c.status === 'published' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : c.status === 'scheduled' ? 'bg-[#4361EE]/10 text-[#4361EE] border border-[#4361EE]/20' : 'bg-[#2A2A2E] text-[#A1A1AA]'}`}>{c.status || "published"}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${c.translation_status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : c.translation_status === 'failed' ? 'bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>{c.translation_status}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === "published" ? (
                          <button data-testid={`draft-content-${c.id}`} onClick={() => handleDraft(c.id)} className="p-1.5 text-[#A1A1AA] hover:text-amber-400 transition-colors" title="Set Draft"><Pause size={14} /></button>
                        ) : (
                          <button data-testid={`publish-content-${c.id}`} onClick={() => handlePublish(c.id)} className="p-1.5 text-[#A1A1AA] hover:text-emerald-400 transition-colors" title="Publish"><Play size={14} /></button>
                        )}
                        <button data-testid={`translate-content-${c.id}`} onClick={() => handleTranslate(c.id)} disabled={translating[c.id]} className="p-1.5 text-[#A1A1AA] hover:text-[#4361EE] transition-colors disabled:opacity-50" title="Translate">
                          {translating[c.id] ? <SpinnerGap size={14} className="animate-spin" /> : <Translate size={14} />}
                        </button>
                        <button data-testid={`delete-content-${c.id}`} onClick={() => handleDeleteContent(c.id)} className="p-1.5 text-[#A1A1AA] hover:text-[#E53935] transition-colors"><Trash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {contents.pages > 1 && (
            <div className="flex justify-center gap-2">
              {Array.from({ length: Math.min(contents.pages, 10) }, (_, i) => (
                <button key={i+1} onClick={() => fetchContents(i+1)} className={`px-3 py-1 text-sm rounded-sm transition-colors ${contents.page === i+1 ? 'bg-[#E53935] text-white' : 'bg-[#141417] text-[#A1A1AA] border border-[#2A2A2E] hover:border-[#3A3A3E]'}`}>{i+1}</button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button data-testid="sync-categories-btn" onClick={handleSyncCategories} disabled={syncingCats} className="bg-[#141417] border border-[#2A2A2E] hover:border-[#4361EE] text-white rounded-sm h-9 px-4 text-sm font-semibold">
              {syncingCats ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Syncing...</> : "Sync from Content"}
            </Button>
            <Button data-testid="add-category-btn" onClick={() => setCategoryDialog(true)} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-4 text-sm font-semibold"><Plus size={14} className="mr-1.5" /> Add Category</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.length === 0 ? <div className="col-span-full text-center py-12 text-sm text-[#555]">No categories yet</div> : categories.map((cat) => (
              <div key={cat.id} className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm hover:border-[#3A3A3E] transition-colors">
                <div className="flex items-start justify-between">
                  <div><div className="text-sm font-medium text-white">{cat.name}</div><div className="text-xs text-[#A1A1AA] mt-0.5">/{cat.slug}</div></div>
                  <button data-testid={`delete-cat-${cat.id}`} onClick={() => handleDeleteCategory(cat.id)} className="p-1 text-[#A1A1AA] hover:text-[#E53935] transition-colors"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Scraper Tab */}
        <TabsContent value="scraper" className="space-y-4">
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Content Scraper</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Source URL</label>
                <Input data-testid="scrape-url-input" value={scrapeForm.url} onChange={e => setScrapeForm({...scrapeForm, url: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Pages to Scrape</label>
                <Input data-testid="scrape-pages-input" type="number" min={1} max={10} value={scrapeForm.pages} onChange={e => setScrapeForm({...scrapeForm, pages: parseInt(e.target.value) || 1})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" />
              </div>
              <div className="flex items-end">
                <Button data-testid="start-scrape-btn" onClick={handleScrape} disabled={scraping} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-10 px-6 font-semibold w-full">
                  {scraping ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Scraping...</> : <><Robot size={14} className="mr-1.5" /> Start Scrape</>}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input type="checkbox" id="dl-thumbs" checked={scrapeForm.download_thumbnails} onChange={e => setScrapeForm({...scrapeForm, download_thumbnails: e.target.checked})} className="rounded-sm" />
              <label htmlFor="dl-thumbs" className="text-xs text-[#A1A1AA]">Download thumbnails to project folder</label>
            </div>
          </div>
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
            <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Job History</h3></div>
            <div className="divide-y divide-[#2A2A2E]">
              {scrapeJobs.length === 0 ? <div className="px-5 py-8 text-center text-sm text-[#555]">No jobs yet</div> : scrapeJobs.map(job => (
                <div key={job.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-white font-medium">{job.type === "scrape" ? "Scrape" : "Batch Translate"}</div>
                    <div className="text-xs text-[#A1A1AA]">{job.source_url || ""} &middot; {new Date(job.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${job.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : job.status === 'running' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/20'}`}>{job.status}</span>
                    {job.new_items !== undefined && <div className="text-xs text-[#A1A1AA] mt-1">{job.new_items} new / {job.total_scraped} scraped</div>}
                    {job.translated_count !== undefined && <div className="text-xs text-[#A1A1AA] mt-1">{job.translated_count} translated</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Site Texts Tab */}
        <TabsContent value="site-texts" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#A1A1AA]">Menu, footer and UI texts for all languages</p>
            <Button data-testid="translate-all-texts-btn" onClick={handleTranslateAllTexts} disabled={translatingTexts} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-4 text-sm font-semibold">
              {translatingTexts ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Translating...</> : <><Globe size={14} className="mr-1.5" /> Translate All Languages</>}
            </Button>
          </div>
          {editingLang ? (
            <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Editing: {editingLang}</h3>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingLang(null)} className="text-[#A1A1AA] hover:text-white rounded-sm h-8 text-xs">Cancel</Button>
                  <Button data-testid="save-texts-btn" onClick={handleSaveSiteTexts} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-8 px-4 text-xs font-semibold">Save</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(editTexts).map(([key, val]) => (
                  <div key={key}>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-[#555] font-bold mb-1 block">{key.replace(/_/g, " ")}</label>
                    <Input value={val} onChange={e => setEditTexts({...editTexts, [key]: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-sm" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {siteTexts.length === 0 ? <div className="col-span-full text-center py-12 text-sm text-[#555]">No translations yet. Create English texts first by editing the project.</div> : siteTexts.map(st => (
                <button key={st.language} data-testid={`edit-texts-${st.language}`} onClick={() => startEditTexts(st.language, st.texts)} className="bg-[#141417] border border-[#2A2A2E] p-4 rounded-sm hover:border-[#4361EE]/40 transition-colors text-left">
                  <div className="text-sm font-medium text-white">{st.language}</div>
                  <div className="text-xs text-[#A1A1AA] mt-1">{Object.keys(st.texts).length} keys</div>
                  <div className="text-[10px] text-[#555] mt-1">Click to edit</div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-4">
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">SEO Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Domain</label><div className="text-sm text-white">{project.domain}</div></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Language</label><div className="text-sm text-white">{project.target_language}</div></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Country</label><div className="text-sm text-white">{project.target_country}</div></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Total Contents</label><div className="text-sm text-white">{contents.total}</div></div>
            </div>
          </div>
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Sitemap</h3>
            <p className="text-sm text-[#A1A1AA] mb-3">Auto-generated sitemap with all published content and categories.</p>
            <a href={`${API}/projects/${projectId}/sitemap.xml`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#4361EE] hover:underline" data-testid="sitemap-link">View Sitemap XML</a>
            <span className="mx-3 text-[#2A2A2E]">|</span>
            <a href={`${API}/projects/${projectId}/robots.txt`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#4361EE] hover:underline" data-testid="robots-link">View robots.txt</a>
          </div>
          {/* Google Search Console */}
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Google Search Console</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Verification Code</label>
                <Input data-testid="gsc-code-input" value={gscConfig.verification_code || ""} onChange={e => setGscConfig({...gscConfig, verification_code: e.target.value})} placeholder="e.g. abc123def456" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-sm" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Property URL</label>
                <Input data-testid="gsc-url-input" value={gscConfig.property_url || ""} onChange={e => setGscConfig({...gscConfig, property_url: e.target.value})} placeholder={`https://${project.domain}`} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-sm" />
              </div>
            </div>
            <Button data-testid="gsc-save-btn" onClick={handleSaveGsc} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-8 px-4 text-xs font-semibold">Save GSC Config</Button>
            <div className="mt-4 space-y-2">
              <p className="text-xs text-[#555]">Add this meta tag to your site's {'<head>'}:</p>
              {gscConfig.verification_code && (
                <code className="block text-xs text-[#4361EE] bg-[#0A0A0C] p-2 rounded-sm break-all">{`<meta name="google-site-verification" content="${gscConfig.verification_code}" />`}</code>
              )}
              <p className="text-xs text-[#555]">Or verify via HTML file: <a href={`${API}/projects/${projectId}/gsc-verification.html`} target="_blank" rel="noopener noreferrer" className="text-[#4361EE] hover:underline">Download verification HTML</a></p>
              <p className="text-xs text-[#555]">Submit your sitemap URL in Search Console: <span className="text-[#4361EE]">https://{project.domain}/sitemap.xml</span></p>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="import" className="space-y-4">
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Bulk CSV/JSON Import</h3>
            <p className="text-xs text-[#555] mb-3">Paste JSON array of items. Each item needs at minimum: <code className="text-[#4361EE]">title</code>. Optional: <code className="text-[#4361EE]">description, thumbnail_url, video_url, duration, tags[], categories[]</code></p>
            <textarea
              data-testid="bulk-import-textarea"
              value={bulkJson}
              onChange={e => setBulkJson(e.target.value)}
              placeholder={'[\n  {"title": "My Video", "video_url": "https://...", "duration": "15 min", "tags": ["tag1"]},\n  {"title": "Another", "thumbnail_url": "https://..."}\n]'}
              className="w-full h-48 bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm p-3 text-sm font-mono resize-y focus:border-[#4361EE] focus:outline-none"
            />
            <div className="flex justify-end mt-3">
              <Button data-testid="bulk-import-btn" onClick={handleBulkImport} disabled={importing || !bulkJson.trim()} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-9 px-6 text-sm font-semibold disabled:opacity-50">
                {importing ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Importing...</> : <><Upload size={14} className="mr-1.5" /> Import</>}
              </Button>
            </div>
          </div>
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">CSV Format Example</h3>
            <pre className="text-xs text-[#A1A1AA] bg-[#0A0A0C] p-3 rounded-sm overflow-x-auto">
{`title,video_url,duration,tags
"Video Title 1","https://example.com/v1","15 min","tag1,tag2"
"Video Title 2","https://example.com/v2","20 min","tag3"`}
            </pre>
            <p className="text-xs text-[#555] mt-2">Convert CSV to JSON first, then paste above. Each row becomes a JSON object.</p>
          </div>
        </TabsContent>

        {/* Template Preview Tab */}
        <TabsContent value="template" className="space-y-4">
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Site Template Preview</h3>
            <p className="text-sm text-[#A1A1AA] mb-4">Auto-generated public site template with dark/light mode toggle, SEO optimization, and translated content.</p>
            <div className="flex gap-3">
              <a href={`${API}/projects/${projectId}/generate-template`} target="_blank" rel="noopener noreferrer" data-testid="preview-template-btn">
                <Button className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-6 text-sm font-semibold">
                  <Eye size={14} className="mr-1.5" /> Preview Template
                </Button>
              </a>
              <Button data-testid="download-template-btn" onClick={async () => {
                try {
                  const resp = await fetch(`${API}/projects/${projectId}/generate-template`, { credentials: 'include' });
                  const html = await resp.text();
                  const blob = new Blob([html], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${project.domain || 'site'}.html`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Template downloaded");
                } catch { toast.error("Download failed"); }
              }} className="bg-[#141417] border border-[#2A2A2E] hover:border-[#4361EE] text-white rounded-sm h-9 px-6 text-sm font-semibold">
                <FileText size={14} className="mr-1.5" /> Download HTML
              </Button>
            </div>
          </div>
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">Template Features</h3>
            <ul className="space-y-2 text-sm text-[#A1A1AA]">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Dark/Light mode toggle with localStorage persistence</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> SEO meta tags (title, description, canonical, sitemap)</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Responsive grid layout (auto-fill columns)</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Translated content ({project.target_language})</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Translated menu/footer from Site Texts</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Video thumbnails with duration badges</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Category navigation</li>
            </ul>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#141417] border border-[#2A2A2E] p-5 rounded-sm">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Total Views</div>
              <div className="text-3xl font-black text-white mt-2" style={{ fontFamily: 'Chivo' }}>{analytics?.total_views || 0}</div>
            </div>
            <div className="bg-[#141417] border border-[#2A2A2E] p-5 rounded-sm">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Last 30 Days</div>
              <div className="text-3xl font-black text-[#4361EE] mt-2" style={{ fontFamily: 'Chivo' }}>{analytics?.recent_views || 0}</div>
            </div>
            <div className="bg-[#141417] border border-[#2A2A2E] p-5 rounded-sm">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">Unique Visitors</div>
              <div className="text-3xl font-black text-emerald-400 mt-2" style={{ fontFamily: 'Chivo' }}>{analytics?.unique_visitors || 0}</div>
            </div>
          </div>

          {/* Daily Views Chart (simple bar) */}
          {analytics?.daily_views?.length > 0 && (
            <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
              <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">Daily Views</h3>
              <div className="flex items-end gap-1 h-32">
                {analytics.daily_views.map((d, i) => {
                  const max = Math.max(...analytics.daily_views.map(x => x.views));
                  const pct = max > 0 ? (d.views / max) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.views} views`}>
                      <span className="text-[9px] text-[#555] opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
                      <div className="w-full bg-[#4361EE] rounded-t-sm transition-all hover:bg-[#5373FF]" style={{ height: `${Math.max(pct, 2)}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[9px] text-[#555]">{analytics.daily_views[0]?.date}</span>
                <span className="text-[9px] text-[#555]">{analytics.daily_views[analytics.daily_views.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {/* Top Contents */}
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
            <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Top Contents</h3></div>
            <div className="divide-y divide-[#2A2A2E]">
              {analytics?.top_contents?.length > 0 ? analytics.top_contents.map((c, i) => (
                <div key={c.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-black text-[#2A2A2E] w-6">{i + 1}</span>
                    <div className="min-w-0"><div className="text-sm text-white font-medium truncate max-w-sm">{c.title}</div></div>
                  </div>
                  <span className="text-sm font-bold text-[#4361EE] whitespace-nowrap">{c.tracked_views} views</span>
                </div>
              )) : <div className="px-5 py-8 text-center text-sm text-[#555]">No view data yet. Views are tracked when visitors open the public template.</div>}
            </div>
          </div>

          {/* Top Referrers */}
          {analytics?.top_referrers?.length > 0 && (
            <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
              <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Top Referrers</h3></div>
              <div className="divide-y divide-[#2A2A2E]">
                {analytics.top_referrers.map((r, i) => (
                  <div key={i} className="px-5 py-2 flex items-center justify-between">
                    <span className="text-xs text-[#A1A1AA] truncate max-w-md">{r.referrer}</span>
                    <span className="text-xs font-bold text-white">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* CDN Tab */}
        <TabsContent value="cdn" className="space-y-4">
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-4">CDN Configuration</h3>
            <p className="text-xs text-[#555] mb-4">All images are stored centrally on the main CMS server. Configure a CDN subdomain to serve images via Nginx reverse proxy. This avoids downloading images separately for each project.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">CDN Subdomain</label>
                <Input data-testid="cdn-subdomain-input" value={cdnConfig.cdn_subdomain || ""} onChange={e => setCdnConfig({...cdnConfig, cdn_subdomain: e.target.value})} placeholder={`cdn.${project.domain}`} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" />
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
                  <input type="checkbox" data-testid="cdn-enabled-toggle" checked={cdnConfig.cdn_enabled || false} onChange={e => setCdnConfig({...cdnConfig, cdn_enabled: e.target.checked})} className="rounded-sm" />
                  CDN Enabled
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="cdn-save-btn" onClick={handleSaveCdn} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-6 text-sm font-semibold">Save CDN Config</Button>
              <Button data-testid="cdn-rewrite-btn" onClick={handleRewriteCdnUrls} disabled={rewritingCdn || !cdnConfig.cdn_enabled} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-9 px-6 text-sm font-semibold disabled:opacity-50">
                {rewritingCdn ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Rewriting...</> : "Rewrite Image URLs to CDN"}
              </Button>
            </div>
          </div>

          {/* Nginx Config */}
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Nginx Reverse Proxy Config</h3>
              <Button data-testid="download-nginx-btn" onClick={async () => {
                try {
                  const resp = await fetch(`${API}/projects/${projectId}/nginx-config`, { credentials: 'include' });
                  const text = await resp.text();
                  const blob = new Blob([text], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${project.domain}.nginx.conf`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Nginx config downloaded");
                } catch { toast.error("Download failed"); }
              }} className="bg-[#141417] border border-[#2A2A2E] hover:border-[#4361EE] text-white rounded-sm h-8 px-3 text-xs font-semibold">
                Download .conf
              </Button>
            </div>
            <div className="bg-[#0A0A0C] rounded-sm p-4 text-xs text-[#A1A1AA] font-mono overflow-x-auto whitespace-pre">
{`# CDN subdomain: ${cdnConfig.cdn_subdomain || `cdn.${project.domain}`}
# Points to central image storage on CMS server
# All project images served from /api/cdn/ path

server {
    server_name ${cdnConfig.cdn_subdomain || `cdn.${project.domain}`};
    location /img/ {
        proxy_pass http://CMS_SERVER:8001/api/cdn/;
        expires 30d;
        add_header Cache-Control "public, immutable";
    }
}`}
            </div>
            <p className="text-xs text-[#555] mt-3">Image URL format: <span className="text-[#4361EE]">https://{cdnConfig.cdn_subdomain || `cdn.${project.domain}`}/img/{'<hash>'}.jpg</span></p>
          </div>

          {/* CDN Stats */}
          <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">Storage</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold">Images in CDN</div>
                <div className="text-xl font-black text-white mt-1" style={{ fontFamily: 'Chivo' }}>{cdnConfig.image_count || 0}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold">Status</div>
                <div className={`text-sm font-bold mt-2 ${cdnConfig.cdn_enabled ? 'text-emerald-400' : 'text-[#555]'}`}>{cdnConfig.cdn_enabled ? "Active" : "Disabled"}</div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* SEO Articles Tab */}
        <TabsContent value="seo-articles" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#A1A1AA]">E-E-A-T compliant SEO articles for homepage and watch pages</p>
            <Button data-testid="generate-seo-btn" onClick={handleGenerateSeoArticles} disabled={generatingSeo} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-9 px-4 text-sm font-semibold">
              {generatingSeo ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Generating...</> : <><Article size={14} className="mr-1.5" /> Generate All SEO Articles</>}
            </Button>
          </div>
          {editingSeoSlug ? (
            <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#A1A1AA]">Editing: {editingSeoSlug === "index" ? "Homepage" : editingSeoSlug}</h3>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingSeoSlug(null)} className="text-[#A1A1AA] rounded-sm h-8 text-xs">Cancel</Button>
                  <Button data-testid="save-seo-article-btn" onClick={handleSaveSeoArticle} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-8 px-4 text-xs font-semibold">Save</Button>
                </div>
              </div>
              <textarea value={editingSeoContent} onChange={e => setEditingSeoContent(e.target.value)} className="w-full bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm p-3 h-64 text-sm resize-y leading-relaxed" />
              <div className="text-xs text-[#555] mt-2">{editingSeoContent.split(/\s+/).filter(Boolean).length} words</div>
            </div>
          ) : (
            <div className="space-y-3">
              {seoArticles.length === 0 ? (
                <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm px-5 py-12 text-center text-sm text-[#555]">No SEO articles yet. Click "Generate All SEO Articles" to create E-E-A-T content.</div>
              ) : seoArticles.map(art => (
                <div key={art.slug} className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-4 hover:border-[#3A3A3E] transition-colors cursor-pointer" onClick={() => { setEditingSeoSlug(art.slug); setEditingSeoContent(art.content); }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${art.page_type === 'homepage' ? 'bg-[#4361EE]/10 text-[#4361EE] border border-[#4361EE]/20' : 'bg-[#2A2A2E] text-[#A1A1AA]'}`}>{art.page_type}</span>
                      <span className="text-sm text-white font-medium">{art.slug === "index" ? "Homepage" : art.slug.substring(0, 40)}</span>
                    </div>
                    <span className="text-xs text-[#555]">{art.content?.split(/\s+/).filter(Boolean).length || 0} words</span>
                  </div>
                  <p className="text-xs text-[#A1A1AA] line-clamp-2">{art.content?.substring(0, 150)}...</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Content Dialog */}
      <Dialog open={contentDialog} onOpenChange={setContentDialog}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-lg rounded-sm">
          <DialogHeader><DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Add Content</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateContent} className="space-y-3 mt-2">
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Title</label><Input data-testid="content-title-input" value={contentForm.title} onChange={e => setContentForm({...contentForm, title: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Description</label><Input data-testid="content-desc-input" value={contentForm.description} onChange={e => setContentForm({...contentForm, description: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Thumbnail URL</label><Input data-testid="content-thumb-input" value={contentForm.thumbnail_url} onChange={e => setContentForm({...contentForm, thumbnail_url: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Video URL</label><Input data-testid="content-video-input" value={contentForm.video_url} onChange={e => setContentForm({...contentForm, video_url: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Duration</label><Input data-testid="content-duration-input" value={contentForm.duration} onChange={e => setContentForm({...contentForm, duration: e.target.value})} placeholder="15 min" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Tags</label><Input data-testid="content-tags-input" value={contentForm.tags} onChange={e => setContentForm({...contentForm, tags: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Categories</label><Input data-testid="content-cats-input" value={contentForm.categories} onChange={e => setContentForm({...contentForm, categories: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setContentDialog(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
              <Button data-testid="content-submit-btn" type="submit" disabled={saving} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm px-6 font-semibold">{saving ? "Adding..." : "Add Content"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-md rounded-sm">
          <DialogHeader><DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Add Category</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-3 mt-2">
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Name</label><Input data-testid="category-name-input" value={categoryForm.name} onChange={e => setCategoryForm({...categoryForm, name: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Slug</label><Input data-testid="category-slug-input" value={categoryForm.slug} onChange={e => setCategoryForm({...categoryForm, slug: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Description</label><Input data-testid="category-desc-input" value={categoryForm.description} onChange={e => setCategoryForm({...categoryForm, description: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setCategoryDialog(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
              <Button data-testid="category-submit-btn" type="submit" disabled={saving} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm px-6 font-semibold">{saving ? "Creating..." : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
